import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Edit2, Trash2, Plus, Search, MoreHorizontal, RefreshCw, Shield, Mail, Crown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface AdminUser {
  id: string;
  username: string;
  full_name: string | null;
  is_active: boolean;
  is_super_admin: boolean;
  created_at: string;
  updated_at: string;
  email?: string;
  last_sign_in_at?: string;
}

const AdminUsersPage = () => {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadAdmins();
    
    // Realtime subscription
    const channel = supabase
      .channel('admin-users-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'admin_users',
        },
        (payload) => {
          console.log('Realtime change detected in admin_users:', payload);
          loadAdmins(searchTerm);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [searchTerm]);

  const loadAdmins = async (search?: string) => {
    if (!isRefreshing) setIsLoading(true);
    try {
      console.log(`Loading admins with search term: "${search || ''}"`);
      
      // Query admin_users with auth.users data
      let query = supabase
        .from('admin_users')
        .select(`
          id,
          username,
          full_name,
          is_active,
          is_super_admin,
          created_at,
          updated_at
        `)
        .order('is_super_admin', { ascending: false })
        .order('created_at', { ascending: false });

      if (search && search.trim()) {
        query = query.or(`username.ilike.%${search}%,full_name.ilike.%${search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Get email and last_sign_in from auth.users
      const adminsWithAuth = await Promise.all(
        (data || []).map(async (admin) => {
          const { data: authData } = await supabase
            .from('auth.users')
            .select('email, last_sign_in_at')
            .eq('id', admin.id)
            .single();

          return {
            ...admin,
            email: authData?.email,
            last_sign_in_at: authData?.last_sign_in_at,
          };
        })
      );

      console.log(`Loaded ${adminsWithAuth.length} admins.`);
      setAdmins(adminsWithAuth);
    } catch (error) {
      console.error('Erro ao carregar administradores:', error);
      toast.error('Erro ao carregar lista de administradores', { duration: 2000 });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadAdmins(searchTerm);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadAdmins(searchTerm);
  };

  const handleEdit = (adminId: string) => {
    navigate(`/admin/admins/edit/${adminId}`);
  };

  const handleToggleStatus = async (adminId: string, currentStatus: boolean, isSuperAdmin: boolean) => {
    if (isSuperAdmin) {
      toast.error('Não é possível desativar um Super Administrador!', { duration: 3000 });
      return;
    }

    try {
      const { error } = await supabase
        .from('admin_users')
        .update({ is_active: !currentStatus, updated_at: new Date().toISOString() })
        .eq('id', adminId);

      if (error) throw error;

      toast.success(
        !currentStatus ? 'Administrador ativado com sucesso' : 'Administrador desativado com sucesso',
        { duration: 1500 }
      );

      // Update local state
      setAdmins(prevAdmins =>
        prevAdmins.map(admin =>
          admin.id === adminId ? { ...admin, is_active: !currentStatus } : admin
        )
      );
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status do administrador', { duration: 2000 });
    }
  };

  const handleDelete = async (adminId: string, adminName: string, isSuperAdmin: boolean) => {
    if (isSuperAdmin) {
      toast.error('Não é possível excluir um Super Administrador!', { duration: 3000 });
      return;
    }

    if (admins.length === 1) {
      toast.error('Não é possível excluir o último administrador!', { duration: 3000 });
      return;
    }

    if (window.confirm(`Tem certeza que deseja excluir o administrador "${adminName}"? Esta ação não pode ser desfeita.`)) {
      try {
        // Delete from admin_users (auth.users will be handled by trigger or manually)
        const { error } = await supabase
          .from('admin_users')
          .delete()
          .eq('id', adminId);

        if (error) throw error;

        toast.success('Administrador excluído com sucesso', { duration: 1500 });
        setAdmins(prevAdmins => prevAdmins.filter(a => a.id !== adminId));
      } catch (error) {
        console.error('Erro ao excluir administrador:', error);
        toast.error('Erro ao excluir administrador', { duration: 2000 });
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-4 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 sm:space-x-2">
        <div className="flex items-center space-x-2">
          <Shield className="h-6 w-6 text-navy-700" />
          <h2 className="text-2xl font-bold">Administradores</h2>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            className="h-9 w-9"
            disabled={isLoading || isRefreshing}
            title="Atualizar lista"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
          <form onSubmit={handleSearch} className="flex flex-grow sm:flex-grow-0 sm:w-64">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nome ou email..."
              className="rounded-r-none h-9"
            />
            <Button type="submit" className="rounded-l-none h-9">
              <Search className="h-4 w-4" />
            </Button>
          </form>
          <Button onClick={() => navigate('/admin/register')} className="h-9">
            <Plus className="mr-2 h-4 w-4" /> Novo Admin
          </Button>
        </div>
      </div>

      {/* Admin List Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-4 px-6">
          <CardTitle className="text-lg">Lista de Administradores</CardTitle>
          <div className="text-sm text-muted-foreground">
            {isLoading ? 'Carregando...' : `${admins.length} administrador${admins.length !== 1 ? 'es' : ''}`}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && admins.length === 0 ? (
            <div className="py-12 text-center">Carregando administradores...</div>
          ) : !isLoading && admins.length === 0 ? (
            <div className="py-12 text-center">
              {searchTerm ? 'Nenhum administrador encontrado para esta busca.' : 'Nenhum administrador cadastrado.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Nível</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Último Acesso</TableHead>
                  <TableHead>Cadastrado em</TableHead>
                  <TableHead className="text-right pr-6">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.id} className={admin.is_super_admin ? 'bg-amber-50/30' : ''}>
                    <TableCell className="font-medium py-3 px-6">
                      <div className="flex items-center space-x-2">
                        {admin.is_super_admin ? (
                          <Crown className="h-4 w-4 text-amber-600" />
                        ) : (
                          <Shield className="h-4 w-4 text-navy-600" />
                        )}
                        <span>{admin.full_name || admin.username}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-3 px-6">
                      <div className="flex items-center space-x-1.5 text-sm">
                        <Mail className="h-3.5 w-3.5 text-gray-500" />
                        <span>{admin.email || admin.username}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-3 px-6">
                      {admin.is_super_admin ? (
                        <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white border-0">
                          <Crown className="h-3 w-3 mr-1" />
                          Super Admin
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-navy-700 border-navy-300">
                          Administrador
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="py-3 px-6">
                      <Badge variant={admin.is_active ? 'default' : 'secondary'}>
                        {admin.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 px-6 text-sm text-muted-foreground">
                      {formatDateTime(admin.last_sign_in_at)}
                    </TableCell>
                    <TableCell className="py-3 px-6 text-sm text-muted-foreground">
                      {formatDate(admin.created_at)}
                    </TableCell>
                    <TableCell className="text-right py-3 pr-6">
                      {admin.is_super_admin ? (
                        <div className="flex items-center justify-end space-x-1 text-xs text-amber-600">
                          <Shield className="h-3.5 w-3.5" />
                          <span>Protegido</span>
                        </div>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Ações</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleEdit(admin.id)}>
                              <Edit2 className="mr-2 h-4 w-4" /> Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleStatus(admin.id, admin.is_active, admin.is_super_admin)}>
                              {admin.is_active ? '🔒 Desativar' : '✅ Ativar'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDelete(admin.id, admin.full_name || admin.username, admin.is_super_admin)}
                              className="text-red-600 focus:text-red-600 focus:bg-red-50"
                              disabled={admins.length === 1}
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUsersPage;
